import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faClock,
  faFlagCheckered,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './RetargetingWatchSection.css';

const PAGE_SIZE = 25;

/**
 * RetargetingWatchSection - Campaign-details section for the MadHive
 * retargeting reach watch (madhiveRetargetingScheduler). A MadHive launch
 * with the retargeting flow creates a retargeting pixel/segment/audience but
 * leaves it untargeted (0 reach at launch would starve delivery); the watch
 * checks the audience's reach hourly and rewrites the campaign's line items
 * to target retargeting once reach > 0. Watches are created automatically at
 * launch — this section is read-only status + run history; it renders
 * nothing when the campaign has no watch.
 */
const RetargetingWatchSection = ({ campaign, accountId, user }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);   // 0-based page index
  const [total, setTotal] = useState(0); // total reach checks for this watch

  // Fetch the current page of reach checks
  const fetchWatch = useCallback(async () => {
    if (!campaign?.id || !accountId) return;

    try {
      setIsLoading(true);
      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/retargeting-watch`),
        {
          params: {
            accountId,
            userId: user?.userId || user?.id,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
          }
        }
      );
      setData(response.data);
      setTotal(response.data.pagination?.total ?? (response.data.runs || []).length);
      setError(null);
    } catch (err) {
      console.error('Error fetching retargeting watch:', err);
      setError('Failed to load retargeting watch history');
    } finally {
      setIsLoading(false);
    }
  }, [campaign?.id, accountId, user, page]);

  // Initial fetch (re-runs when the page changes)
  useEffect(() => {
    fetchWatch();
  }, [fetchWatch]);

  // Clamp the page if the total shrinks (e.g. a newer watch with fewer runs).
  useEffect(() => {
    const maxPage = Math.max(Math.ceil(total / PAGE_SIZE) - 1, 0);
    if (page > maxPage) setPage(maxPage);
  }, [total, page]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getWatchBadge = (watch) => {
    switch (watch.status) {
      case 'active':
        return watch.consecutiveErrors > 0 ? (
          <span className="watch-badge warning" title={watch.lastError || undefined}>
            <FontAwesomeIcon icon={faExclamationCircle} /> Check errors ({watch.consecutiveErrors})
          </span>
        ) : (
          <span className="watch-badge active">
            <FontAwesomeIcon icon={faClock} /> Watching hourly
          </span>
        );
      case 'completed':
        return (
          <span className="watch-badge completed">
            <FontAwesomeIcon icon={faCheckCircle} /> Retargeting enabled
          </span>
        );
      case 'failed':
        return (
          <span className="watch-badge failed" title={watch.lastError || undefined}>
            <FontAwesomeIcon icon={faExclamationCircle} /> Stopped after repeated errors
          </span>
        );
      case 'expired':
        return (
          <span className="watch-badge inactive">
            <FontAwesomeIcon icon={faFlagCheckered} /> Expired (reach never materialized)
          </span>
        );
      case 'cancelled':
        return (
          <span className="watch-badge inactive">
            <FontAwesomeIcon icon={faBan} /> Cancelled
          </span>
        );
      default:
        return <span className="watch-badge inactive">{watch.status}</span>;
    }
  };

  const getOutcomeBadge = (run) => {
    switch (run.outcome) {
      case 'enabled':
        return (
          <span className="run-outcome enabled">
            <FontAwesomeIcon icon={faCheckCircle} /> Enabled
          </span>
        );
      case 'waiting':
        // The service persists the waiting note (e.g. "reach 42 but no line
        // items were found to update") in the run's error column — surface it.
        return (
          <span className="run-outcome waiting" title={run.error || undefined}>
            <FontAwesomeIcon icon={faClock} /> Reach not ready
          </span>
        );
      case 'error':
        return (
          <span className="run-outcome error" title={run.error || undefined}>
            <FontAwesomeIcon icon={faExclamationCircle} /> Error
          </span>
        );
      case 'dispatched':
        return (
          <span className="run-outcome dispatched">
            <FontAwesomeIcon icon={faSpinner} spin /> Checking…
          </span>
        );
      default:
        // An outcome this build doesn't know must not render as in-flight.
        return <span className="run-outcome waiting">{run.outcome}</span>;
    }
  };

  const formatLineItems = (run) => {
    if (run.outcome !== 'enabled') return '-';
    const updated = run.lineItemsUpdated ?? 0;
    const skipped = run.lineItemsSkipped ?? 0;
    return `${updated} updated${skipped ? `, ${skipped} already targeting` : ''}`;
  };

  // No watch = the launch didn't create a retargeting audience (or predates
  // the feature) — this section simply doesn't apply. Errors are shown so a
  // broken fetch doesn't masquerade as "no watch".
  if (!isLoading && !error && !data?.watch) return null;

  const watch = data?.watch || null;
  const runs = data?.runs || [];

  return (
    <div className="retargeting-watch-section">
      <div className="section-header">
        <h3><FontAwesomeIcon icon={faBullseye} /> Retargeting Reach Watch</h3>
        <div className="header-actions">
          {!isLoading && !error && watch && getWatchBadge(watch)}
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading retargeting watch...
        </div>
      ) : error ? (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationCircle} /> {error}
        </div>
      ) : (
        <>
          <div className="watch-meta">
            <span>MadHive campaign: <span className="monospace">{watch.madhiveCampaignId}</span></span>
            {watch.rtgSegmentName && <span title={watch.retargetingSegmentId || undefined}>Segment: {watch.rtgSegmentName}</span>}
            <span title="The standalone retargeting audience created at launch">
              Audience: <span className="monospace">{watch.retargetingAudienceId}</span>
            </span>
            <span>Last reach: {watch.lastReach ?? 'not checked yet'}</span>
            <span>Checks: {watch.runCount || 0}</span>
            {watch.status === 'active' && <span>Next check: {formatDateTime(watch.nextRunAt)}</span>}
            {watch.status === 'active' && watch.expiresAt && <span>Watching until: {formatDateTime(watch.expiresAt)}</span>}
          </div>

          {watch.status === 'failed' && watch.lastError && (
            <div className="error-message">
              <FontAwesomeIcon icon={faExclamationCircle} /> {watch.lastError}
            </div>
          )}

          {runs.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faBullseye} size="2x" style={{ color: '#9ca3af', marginBottom: '8px' }} />
              <p>No reach checks yet</p>
              <p style={{ fontSize: '12px', color: '#6b7280' }}>
                The retargeting audience launches with 0 reach. Its reach is checked
                hourly (first check shortly after launch); once it is above 0, every
                line item is rewritten to target retargeting alongside its existing
                audience, and each check is listed here.
              </p>
            </div>
          ) : (
            <div className="runs-table-container">
              <table className="runs-table">
                <thead>
                  <tr>
                    <th>Check</th>
                    <th>Time</th>
                    <th>Reach</th>
                    <th>Result</th>
                    <th>Line Items</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => (
                    <tr key={run.id}>
                      <td>#{run.runNumber}</td>
                      <td>{formatDateTime(run.finalizedAt || run.dispatchedAt)}</td>
                      <td>{run.reach ?? '-'}</td>
                      <td>{getOutcomeBadge(run)}</td>
                      <td>{formatLineItems(run)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {total > PAGE_SIZE && (
                <div
                  className="runs-pagination"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', gap: '12px', flexWrap: 'wrap' }}
                >
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + runs.length} of {total}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setPage((p) => Math.max(p - 1, 0))}
                      disabled={page === 0}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: '13px' }}>
                      Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
                    </span>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={(page + 1) * PAGE_SIZE >= total}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RetargetingWatchSection;
